<template>
  <div class="flex flex-col min-h-0">
    <!-- Toolbar -->
    <div
      class="flex flex-wrap items-center justify-between gap-2 pb-2.5 mb-3 border-b shrink-0 border-slate-200/70 dark:border-slate-700/70"
    >
      <div class="flex flex-wrap items-center gap-2">
        <h3
          class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
        >
          {{ t("ticket_timeline") }}
        </h3>
        <span
          v-if="totalCount"
          class="inline-flex items-center justify-center h-5 px-2 text-[10px] font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
        >
          {{ totalCount }}
        </span>
        <span
          v-if="lastUpdatedLabel"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-primary/10 text-[10px] font-medium text-primary"
        >
          <span class="relative flex w-1.5 h-1.5">
            <span
              class="absolute inline-flex w-full h-full rounded-full opacity-75 bg-primary animate-ping"
            ></span>
            <span
              class="relative inline-flex w-1.5 h-1.5 rounded-full bg-primary"
            ></span>
          </span>
          {{ lastUpdatedLabel }}
        </span>
      </div>

      <button
        v-if="items.length > 3"
        type="button"
        class="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium border rounded-lg border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-900/60 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        @click="scrollToTop"
      >
        <i class="mdi mdi-arrow-up text-[14px]"></i>
        {{ topLabel }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading && !items.length" class="py-12 text-center">
      <i class="mdi mdi-loading mdi-spin text-2xl text-primary/60"></i>
      <p class="mt-2 text-xs text-slate-500">{{ t("loading") }}</p>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!items.length"
      class="flex flex-col items-center justify-center py-12 text-center border border-dashed rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
    >
      <span
        class="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400"
      >
        <i class="text-2xl mdi mdi-history"></i>
      </span>
      <p class="text-xs text-slate-500">{{ t("ticket_timeline_empty") }}</p>
    </div>

    <template v-else>
      <div
        ref="scrollContainer"
        class="relative flex-1 min-h-0 overflow-y-auto custom-scrollbar"
        :class="scrollClass"
      >
        <ol class="relative pb-1 space-y-2.5">
          <span
            v-if="items.length > 1"
            aria-hidden="true"
            class="absolute w-px pointer-events-none top-6 bottom-6 start-[18px] bg-slate-200 dark:bg-slate-700/70"
          ></span>

          <template v-for="row in rows" :key="row.key">
            <!-- Day separator -->
            <li v-if="row.type === 'day'" class="relative flex items-center gap-3">
              <span
                class="relative z-10 flex items-center justify-center bg-white rounded-full w-9 h-9 dark:bg-slate-900 text-slate-400"
              >
                <i class="mdi mdi-calendar-blank-outline text-[15px]"></i>
              </span>
              <span
                class="px-2.5 py-0.5 text-[10px] font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
              >
                {{ row.label }}
              </span>
            </li>

            <!-- Event -->
            <li v-else class="relative flex gap-3">
              <div class="shrink-0">
                <span
                  class="relative z-10 flex items-center justify-center rounded-full w-9 h-9 ring-4 ring-white dark:ring-slate-900"
                  :class="actionIconClass(row.item)"
                >
                  <i :class="actionIcon(row.item)" class="text-[16px]"></i>
                </span>
              </div>

              <article
                class="flex-1 min-w-0 p-3.5 bg-white border shadow-sm dark:bg-slate-900/60 rounded-xl transition-colors hover:border-primary/30"
                :class="
                  row.index === 0
                    ? 'border-primary/25 ring-1 ring-primary/10'
                    : 'border-slate-200 dark:border-slate-700'
                "
              >
                <header class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    class="text-[13px] font-semibold text-slate-800 dark:text-slate-100"
                  >
                    {{ headlineText(row.item) }}
                  </span>

                  <span
                    v-if="actorName(row.item)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-lg shrink-0 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  >
                    <i class="mdi mdi-account-outline text-[12px]"></i>
                    {{ actorName(row.item) }}
                  </span>

                  <span
                    v-if="row.index === 0"
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-lg shrink-0 bg-primary/10 text-primary"
                  >
                    {{ latestLabel }}
                  </span>

                  <time
                    class="text-[10px] text-slate-400 tabular-nums"
                    :class="lang === 'ar' ? 'mr-auto' : 'ml-auto'"
                    :datetime="eventDate(row.item)"
                    :title="absoluteTime(row.item)"
                  >
                    {{ relativeTime(row.item) }}
                  </time>
                </header>

                <p
                  v-if="bodyText(row.item)"
                  class="mt-1.5 text-[12px] leading-relaxed break-words whitespace-pre-wrap text-slate-600 dark:text-slate-300"
                  :class="
                    isLong(row.item) && !expanded[row.index] ? 'line-clamp-3' : ''
                  "
                >
                  {{ bodyText(row.item) }}
                </p>

                <div
                  v-if="noteText(row.item)"
                  class="mt-2 pt-2 border-t border-slate-200/70 dark:border-slate-700/70"
                >
                  <p
                    class="text-[10px] font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400"
                  >
                    {{ noteLabel }}
                  </p>
                  <p
                    class="mt-1 text-[12px] leading-relaxed break-words whitespace-pre-wrap text-slate-600 dark:text-slate-300"
                    :class="
                      isLongNote(row.item) && !expanded[row.index]
                        ? 'line-clamp-3'
                        : ''
                    "
                  >
                    {{ noteText(row.item) }}
                  </p>
                </div>

                <button
                  v-if="isLong(row.item) || isLongNote(row.item)"
                  type="button"
                  class="mt-1.5 text-[10px] font-medium text-primary hover:underline"
                  @click="toggleExpand(row.index)"
                >
                  {{ expanded[row.index] ? showLessLabel : showMoreLabel }}
                </button>
              </article>
            </li>
          </template>
        </ol>
      </div>

      <div v-if="hasMore" class="pt-3 shrink-0">
        <button
          type="button"
          class="w-full px-4 py-2 text-xs font-medium transition-colors border rounded-lg border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 hover:border-primary/40 hover:text-primary disabled:opacity-60"
          :disabled="loading"
          @click="$emit('load-more')"
        >
          <i
            class="mdi text-[14px]"
            :class="[
              loading ? 'mdi-loading mdi-spin' : 'mdi-chevron-down',
              lang === 'ar' ? 'ml-1' : 'mr-1',
            ]"
          ></i>
          {{ loading ? t("loading") : t("ticket_load_more") }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from "vue";
import {
  formatTicketDateTime,
  formatTicketDayDate,
  formatTicketRelativeTime,
  ticketDayKey,
  ticketDayOffset,
} from "@/utils/ticket";

const props = defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  totalCount: { type: Number, default: 0 },
  lang: { type: String, default: "ar" },
  t: { type: Function, required: true },
  scrollClass: { type: String, default: "max-h-[min(520px,60vh)]" },
});

defineEmits(["load-more"]);

/** Matched against action codes and the human description, in priority order. */
const ACTION_MATCHERS = [
  { key: "comment", pattern: /comment|تعليق/i },
  { key: "create", pattern: /create|أنشأ|انشأ|إنشاء|انشاء/i },
  { key: "reopen", pattern: /reopen|إعادة فتح|اعادة فتح/i },
  { key: "escalate", pattern: /escalat|تصعيد|صعّد|صعد/i },
  { key: "resolve", pattern: /resolv|تم الحل|حلّ/i },
  { key: "assign", pattern: /assign|إسناد|اسناد|تعيين|أسند/i },
  { key: "transfer", pattern: /transfer|تحويل|حوّل/i },
  { key: "close", pattern: /close|إغلاق|اغلاق|أغلق/i },
  { key: "status", pattern: /status|الحالة/i },
];

const scrollContainer = ref(null);
const expanded = reactive({});

function tr(key, ar, en) {
  const translated = props.t?.(key);
  if (translated && translated !== key) return translated;
  return props.lang === "ar" ? ar : en;
}

const topLabel = computed(() => tr("ticket_timeline_top", "الأعلى", "Top"));
const latestLabel = computed(() => tr("ticket_event_latest", "الأحدث", "Latest"));
const todayLabel = computed(() => tr("ticket_today", "اليوم", "Today"));
const yesterdayLabel = computed(() => tr("ticket_yesterday", "أمس", "Yesterday"));

const showMoreLabel = computed(() =>
  tr("ticket_show_more", "عرض المزيد", "Show more"),
);

const showLessLabel = computed(() =>
  tr("ticket_show_less", "إخفاء", "Show less"),
);

const noteLabel = computed(() =>
  tr("ticket_event_note", "ملاحظة", "Note"),
);

const lastUpdatedLabel = computed(() => {
  const newest = props.items[0];
  if (!newest) return "";
  return formatTicketRelativeTime(eventDate(newest), props.lang);
});

/** Flat list of day separators interleaved with events. */
const rows = computed(() => {
  const out = [];
  let currentDay = null;

  props.items.forEach((item, index) => {
    const key = ticketDayKey(eventDate(item));
    if (key !== currentDay) {
      currentDay = key;
      out.push({
        type: "day",
        key: `day-${key}-${index}`,
        label: dayLabel(eventDate(item)),
      });
    }
    out.push({
      type: "event",
      key: item.id || `act-${index}`,
      item,
      index,
    });
  });

  return out;
});

function eventDate(item) {
  return item.created_at || item.date;
}

function absoluteTime(item) {
  return formatTicketDateTime(eventDate(item), props.lang);
}

function relativeTime(item) {
  return formatTicketRelativeTime(eventDate(item), props.lang);
}

function dayLabel(value) {
  const offset = ticketDayOffset(value);
  if (offset === 0) return todayLabel.value;
  if (offset === 1) return yesterdayLabel.value;
  return formatTicketDayDate(value, props.lang);
}

function actorName(item) {
  const meta = activityProperties(item);
  const name = String(
    item.causer?.name ||
      item.causer?.full_name ||
      item.causer_name ||
      item.actor_name ||
      item.user?.name ||
      item.user?.full_name ||
      item.handler?.name ||
      item.handler_name ||
      meta.causer_name ||
      meta.actor_name ||
      meta.actor ||
      meta.handler_name ||
      "",
  ).trim();

  if (name) return name;

  const action = String(
    item.action || item.type || item.event || item.description || "",
  ).toLowerCase();

  if (/system|automated|auto|النظام/i.test(action)) {
    return tr("ticket_actor_system", "النظام", "System");
  }

  return "";
}

/** Spatie activity log stores extra fields (including notes) in `properties`. */
function activityProperties(item) {
  const raw = item?.properties ?? item?.meta ?? item?.data;
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  return {};
}

function eventDescription(item) {
  return String(item.description || "").trim();
}

function eventNote(item) {
  const props = activityProperties(item);
  const candidates = [
    item.note,
    item.notes,
    props.note,
    props.notes,
    props.comment,
    props.body,
    props.message,
    props.reason,
    props.reopen_note,
    props.escalation_note,
    props.escalate_note,
  ];

  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function actionKey(item) {
  const haystack = [
    item.action,
    item.type,
    item.event,
    item.action_label,
    eventDescription(item),
    eventNote(item),
  ]
    .filter(Boolean)
    .join(" ");

  const match = ACTION_MATCHERS.find(({ pattern }) => pattern.test(haystack));
  return match ? match.key : "default";
}

function humanize(value) {
  return String(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Titles fall back through: API label, known action type, raw code.
 * Returns "" when nothing meaningful exists so the description can lead.
 */
function eventTitle(item) {
  const label = String(item.action_label || "").trim();
  if (label) return label;

  const key = actionKey(item);
  if (key !== "default") {
    const titles = {
      comment: tr("ticket_event_comment", "تعليق جديد", "New comment"),
      create: tr("ticket_event_create", "إنشاء التذكرة", "Ticket created"),
      reopen: tr("ticket_event_reopen", "إعادة فتح", "Reopened"),
      escalate: tr("ticket_event_escalate", "تصعيد", "Escalated"),
      resolve: tr("ticket_event_resolve", "تم الحل", "Resolved"),
      assign: tr("ticket_event_assign", "إسناد", "Assigned"),
      transfer: tr("ticket_event_transfer", "تحويل", "Transferred"),
      close: tr("ticket_event_close", "إغلاق", "Closed"),
      status: tr("ticket_event_status", "تغيير الحالة", "Status changed"),
    };
    return titles[key] || "";
  }

  const raw = item.action || item.type || item.event;
  return raw ? humanize(raw) : "";
}

function headlineText(item) {
  return eventTitle(item) || eventDescription(item) || eventNote(item);
}

function bodyText(item) {
  const description = eventDescription(item);
  if (!description) return "";
  const title = eventTitle(item);
  const note = eventNote(item);
  if (!title) return "";
  if (description === title) return "";
  // Note block owns the user note when both fields carry the same text.
  if (note && description === note) return "";
  return description;
}

function noteText(item) {
  const note = eventNote(item);
  if (!note) return "";
  const title = eventTitle(item);
  const description = eventDescription(item);
  if (!title && !description) return "";
  if (!title && description === note) return "";
  return note;
}

function isLong(item) {
  return bodyText(item).length > 180;
}

function isLongNote(item) {
  return noteText(item).length > 180;
}

function toggleExpand(index) {
  expanded[index] = !expanded[index];
}

function scrollToTop() {
  scrollContainer.value?.scrollTo({ top: 0, behavior: "smooth" });
}

function actionIcon(item) {
  const map = {
    comment: "mdi mdi-comment-text-outline",
    reopen: "mdi mdi-replay",
    escalate: "mdi mdi-arrow-up-bold-circle-outline",
    resolve: "mdi mdi-check-circle-outline",
    assign: "mdi mdi-account-check-outline",
    transfer: "mdi mdi-swap-horizontal",
    create: "mdi mdi-ticket-confirmation-outline",
    close: "mdi mdi-lock-outline",
    status: "mdi mdi-flag-outline",
    default: "mdi mdi-history",
  };
  return map[actionKey(item)] || map.default;
}

function actionIconClass(item) {
  const map = {
    comment: "bg-primary/10 text-primary",
    reopen: "bg-secondary/20 text-secondary",
    escalate:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    resolve:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    assign: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    transfer:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    create: "bg-primary/10 text-primary",
    close: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    status:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
    default:
      "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300",
  };
  return map[actionKey(item)] || map.default;
}
</script>
